import { Dispatch, SetStateAction } from 'react';

import { Flex, Form, FormListFieldData } from 'antd';
import { ColumnProps } from 'antd/es/table';
import dayjs from 'dayjs';

import DatePickerFormItem from '@/components/form/form-items/default-items/date-picker-form-item/DatePickerFormItem';
import SelectFormItem from '@/components/form/form-items/default-items/select-form-item/SelectFormItem';

import useIsMobile from '@/hooks/useIsMobile';

import { DEFAULT_DATE_UAS_FORMAT_MOBILE } from '@/constants/defaultDateFormat';
import { YES_OR_NO_TYPE_OPTIONS } from '@/constants/formFieldOptions';

import DeleteIcon from '@/assets/icons/upload-files/delete.svg?react';

import { InputFormItem, MoneyAmountFormItem, NumberInputFormItem } from '@/components';
import { getFormattedAmount, isNil } from '@/utils';

type TUseGetDebtsTableColumnsProps = {
	editingRowId: number | null;
	setIsDeleteModalOpen: Dispatch<SetStateAction<number | null>>;
};

export const useGetDebtsTableColumns = ({
	editingRowId,
	setIsDeleteModalOpen,
}: TUseGetDebtsTableColumnsProps): ((
	remove: (index: number) => void,
) => ColumnProps<FormListFieldData>[]) => {
	const form = Form.useFormInstance();
	const isMobile = useIsMobile();

	const businessDebts = Form.useWatch('businessDebts', form) || [];
	const isInitialRow = businessDebts.length === 1;

	const commonColumnProps = {
		align: 'left' as const,
		onHeaderCell: () => ({
			style: { textAlign: 'center' as const },
		}),
	};

	return (remove: (index: number) => void) => [
		{
			title: 'Lender/Debtor',
			dataIndex: 'lenderDebtor',
			key: 'lenderDebtor',
			...commonColumnProps,
			onCell: () => ({ style: { minWidth: 170 } }),
			render: (_, field: FormListFieldData, index: number) => {
				if (field.name === editingRowId || isInitialRow) {
					return (
						<InputFormItem
							formItemProps={{
								name: [field.name, 'lenderName'],
								rules: [{ required: false }],
								style: { marginBottom: 0 },
							}}
							inputProps={{
								autoComplete: 'given-name',
								maxLength: 255,
							}}
						/>
					);
				}
				return businessDebts?.[index]?.lenderName;
			},
		},
		{
			title: 'Approximate date of loan disbursement',
			dataIndex: 'fundsReceivedDate',
			key: 'fundsReceivedDate',
			...commonColumnProps,
			onCell: () => ({ style: { minWidth: 140 } }),
			render: (_, field: FormListFieldData, index: number) => {
				if (field.name === editingRowId || isInitialRow) {
					return (
						<DatePickerFormItem
							datePickerProps={{
								placeholder: ' ',
								allowClear: false,
								format: DEFAULT_DATE_UAS_FORMAT_MOBILE,
							}}
							marginBottom={0}
							name={[field.name, 'fundsReceivedDate']}
							rules={[{ required: false }]}
						/>
					);
				}
				const date = businessDebts?.[index]?.fundsReceivedDate;

				return !isNil(date) && dayjs(date).isValid()
					? dayjs(date).format(DEFAULT_DATE_UAS_FORMAT_MOBILE)
					: '';
			},
		},
		{
			title: 'Full amount of the loan received',
			dataIndex: 'totalFunding',
			key: 'totalFunding',
			...commonColumnProps,
			onCell: () => ({ style: { minWidth: 140 } }),
			render: (_, field: FormListFieldData, index: number) => {
				if (field.name === editingRowId || isInitialRow) {
					return (
						<MoneyAmountFormItem
							controls={false}
							marginBottom={0}
							name={[field.name, 'totalFunding']}
							placeholder=""
							rules={[{ required: false }]}
							suffix={null}
						/>
					);
				}
				return getFormattedAmount(businessDebts?.[index]?.totalFunding);
			},
		},
		{
			title: 'Loan repayment term (Months)',
			dataIndex: 'loanTerm',
			key: 'loanTerm',
			...commonColumnProps,
			onCell: () => ({ style: { minWidth: 102 } }),
			render: (_, field: FormListFieldData, index: number) => {
				if (field.name === editingRowId || isInitialRow) {
					return (
						<NumberInputFormItem
							controls={false}
							marginBottom={0}
							max={12}
							name={[field.name, 'loanTerm']}
							rules={[{ required: false }]}
						/>
					);
				}
				return businessDebts?.[index]?.loanTerm;
			},
		},
		{
			title: 'Annual Interest Rate',
			dataIndex: 'interestRate',
			key: 'interestRate',
			...commonColumnProps,
			onCell: () => ({ style: { minWidth: 110 } }),
			render: (_, field: FormListFieldData, index: number) => {
				if (field.name === editingRowId || isInitialRow) {
					return (
						<NumberInputFormItem
							controls={false}
							marginBottom={0}
							name={[field.name, 'interestRate']}
							rules={[{ required: false }]}
						/>
					);
				}
				const value = businessDebts?.[index]?.interestRate;
				return !isNil(value) ? `${value}%` : '';
			},
		},
		{
			title: 'Current Debt Balance**',
			dataIndex: 'outstandingBalance',
			key: 'outstandingBalance',
			...commonColumnProps,
			onCell: () => ({ style: { minWidth: 110 } }),
			render: (_, field: FormListFieldData, index: number) => {
				if (field.name === editingRowId || isInitialRow) {
					return (
						<MoneyAmountFormItem
							controls={false}
							marginBottom={0}
							name={[field.name, 'outstandingBalance']}
							placeholder=""
							rules={[{ required: false }]}
							suffix={null}
						/>
					);
				}
				return getFormattedAmount(businessDebts?.[index]?.outstandingBalance);
			},
		},
		{
			title: 'Monthly Payment',
			dataIndex: 'regularPayment',
			key: 'regularPayment',
			...commonColumnProps,
			onCell: () => ({ style: { minWidth: 110 } }),
			render: (_, field: FormListFieldData, index: number) => {
				if (field.name === editingRowId || isInitialRow) {
					return (
						<MoneyAmountFormItem
							controls={false}
							marginBottom={0}
							name={[field.name, 'regularPayment']}
							placeholder=""
							rules={[{ required: false }]}
							suffix={null}
						/>
					);
				}
				return getFormattedAmount(businessDebts?.[index]?.regularPayment);
			},
		},
		{
			title: 'Annual Payment Schedule',
			dataIndex: 'annualPaymentAmount',
			key: 'annualPaymentAmount',
			...commonColumnProps,
			onCell: () => ({ style: { minWidth: 110 } }),
			render: (_, field: FormListFieldData, index: number) => {
				if (field.name === editingRowId || isInitialRow) {
					return (
						<MoneyAmountFormItem
							controls={false}
							marginBottom={0}
							name={[field.name, 'annualPaymentAmount']}
							placeholder=""
							rules={[{ required: false }]}
							suffix={null}
						/>
					);
				}
				return getFormattedAmount(businessDebts?.[index]?.annualPaymentAmount);
			},
		},
		{
			title: 'SBA Is this an SBA Loan? (Y/N)',
			dataIndex: 'isSBALoan',
			key: 'isSBALoan',
			...commonColumnProps,
			onCell: () => ({ style: { minWidth: 110 } }),
			render: (_, field: FormListFieldData, index: number) => {
				if (field.name === editingRowId || isInitialRow) {
					return (
						<SelectFormItem
							marginBottom={0}
							name={[field.name, 'isSBALoan']}
							options={YES_OR_NO_TYPE_OPTIONS}
							placeholder=""
							rules={[{ required: false }]}
						/>
					);
				}

				const value = businessDebts?.[index]?.isSBALoan;

				if (value === true) return 'Yes';
				if (value === false) return 'No';
				return '';
			},
		},
		{
			title: 'Preference for refinancing / consolidating debt',
			dataIndex: 'isOpenToRefinance',
			key: 'isOpenToRefinance',
			align: 'center',
			onCell: () => ({ style: { minWidth: 110 } }),
			render: (_, field: FormListFieldData, index: number) => {
				if (field.name === editingRowId || isInitialRow) {
					return (
						<SelectFormItem
							marginBottom={0}
							name={[field.name, 'isOpenToRefinance']}
							options={YES_OR_NO_TYPE_OPTIONS}
							placeholder=""
							rules={[{ required: false }]}
						/>
					);
				}
				const value = businessDebts?.[index]?.isOpenToRefinance;

				if (value === true) return 'Yes';
				if (value === false) return 'No';
				return '';
			},
		},
		{
			onCell: () => ({ style: { minWidth: 56 } }),
			title: isMobile ? 'Actions' : '',
			fixed: 'right',
			dataIndex: 'action',
			key: 'action',
			render: (_, field: FormListFieldData) => {
				return !isInitialRow ? (
					<Flex justify="center" style={{ width: '100%' }}>
						<DeleteIcon
							style={{ cursor: 'pointer' }}
							onClick={() => {
								const isDebt =
									businessDebts?.[field.name] &&
									Object.keys(businessDebts?.[field.name]).length > 0;

								if (isDebt) {
									setIsDeleteModalOpen(field.name);
								} else {
									remove(field.name);
								}
							}}
						/>
					</Flex>
				) : null;
			},
		},
	];
};
